import uniq from 'lodash/uniq';
import type { Language } from 'src/enums/language';
import { getIsoLanguageCodeFromLanguage, LanguageIsoCode } from 'src/enums/language';

type JsonValue = string | number | boolean | null | undefined;

type JsonObject = {
  [key: string]: JsonObject | JsonValue | JsonObject[] | JsonValue[];
};

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? U extends object
      ? RecursivePartial<U>[]
      : U[]
    : T[P] extends object
      ? RecursivePartial<T[P]>
      : T[P];
};

const isJsonValue = (value: unknown): value is JsonValue =>
  value === null || typeof value !== 'object';

const isJsonValueArray = (object: unknown): object is JsonValue[] =>
  Array.isArray(object) && object.every(isJsonValue);

const isJsonObjectArray = (object: (JsonValue | JsonObject)[]): object is (JsonObject | null)[] =>
  Array.isArray(object) && object.every((value) => typeof value === 'object');

export const overrideObjectWithTranslationData = <T = JsonObject>(
  initialObject: T,
  overrideObject: RecursivePartial<T>
): T => {
  if (overrideObject == null) {
    return initialObject;
  }

  const newObject = {
    ...initialObject,
  };

  const keys = uniq([
    ...Object.keys(initialObject as Object),
    ...Object.keys(overrideObject),
  ]) as (keyof T)[];

  keys.forEach((key) => {
    if (!(key in overrideObject) || overrideObject[key] == null) {
      return;
    }

    const value = initialObject[key];
    const newValue = overrideObject[key];

    if (isJsonValue(value) && isJsonValue(newValue)) {
      newObject[key] = newValue as T[typeof key];
      return;
    }

    if (Array.isArray(value) && Array.isArray(newValue)) {
      if (newValue.length === 0) {
        return;
      }

      if (isJsonValueArray(value) && isJsonValueArray(newValue)) {
        newObject[key] = newValue as T[typeof key];
      } else if (isJsonObjectArray(value) && isJsonObjectArray(newValue)) {
        newObject[key] = value.map((v, index) =>
          v !== null ? overrideObjectWithTranslationData(v, newValue[index]) : v
        ) as T[typeof key];
      } else {
        throw new Error(
          'Unexpected mix of primitive and object types in object and translation data'
        );
      }
      return;
    }

    if (!isJsonValue(value) && !Array.isArray(value) && typeof newValue === 'object' && newValue) {
      newObject[key] = overrideObjectWithTranslationData<T[typeof key]>(value, newValue);
    }
  });

  return newObject;
};

export const DEFAULT_TRANSLATION_LANGUAGE_CODE = LanguageIsoCode.EN;

export const obtainTranslationDataForLanguage = <T>(
  translationData: Record<LanguageIsoCode, T>,
  language: Language
): T => {
  const languageIsoCode = getIsoLanguageCodeFromLanguage(language);
  const mainContent = translationData[DEFAULT_TRANSLATION_LANGUAGE_CODE];

  if (languageIsoCode === DEFAULT_TRANSLATION_LANGUAGE_CODE) {
    return mainContent;
  }

  const alternateLanguageContent = translationData[languageIsoCode];

  return overrideObjectWithTranslationData(
    mainContent,
    alternateLanguageContent as RecursivePartial<T>
  );
};
